import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, LogIn, Eye, EyeOff, Gamepad2, AlertCircle, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await login(identifier, password);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  const [particles] = useState(() =>
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 15,
      duration: 10 + Math.random() * 10,
      color: ['#00f5ff', '#ff00ff', '#00ff88', '#ffff00'][Math.floor(Math.random() * 4)]
    }))
  );

  // Determine if input looks like an email
  const isEmail = identifier.includes('@');

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Rajdhani:wght@300;400;500;600;700&display=swap');
        
        @keyframes float {
          0%, 100% { transform: translateY(100vh) translateX(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-100vh) translateX(100px); opacity: 0; }
        }
        
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes gridMove {
          0% { transform: perspective(500px) rotateX(60deg) translateY(0); }
          100% { transform: perspective(500px) rotateX(60deg) translateY(50px); }
        }
        
        @keyframes scanLine {
          0% { top: -10%; }
          100% { top: 110%; }
        }
        
        @keyframes logoGlow {
          0% { filter: drop-shadow(0 0 10px rgba(0, 245, 255, 0.5)); }
          100% { filter: drop-shadow(0 0 20px rgba(255, 0, 255, 0.5)); }
        }
        
        @keyframes borderPulse {
          0%, 100% { border-color: rgba(0, 245, 255, 0.3); }
          50% { border-color: rgba(255, 0, 255, 0.5); }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Rajdhani', sans-serif; }
        
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-text-fill-color: #fff;
          -webkit-box-shadow: 0 0 0px 1000px rgba(0, 245, 255, 0.1) inset;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: '#0a0a0f',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        {/* Animated Grid Background */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundImage: 'linear-gradient(rgba(0, 245, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 245, 255, 0.03) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          animation: 'gridMove 20s linear infinite',
          pointerEvents: 'none',
          zIndex: 0
        }} />

        {/* Particles */}
        {particles.map(p => (
          <div
            key={p.id}
            style={{
              position: 'fixed',
              width: '4px',
              height: '4px',
              borderRadius: '50%',
              left: `${p.left}%`,
              animationName: 'float',
              animationDelay: `${p.delay}s`,
              animationDuration: `${p.duration}s`,
              animationTimingFunction: 'linear',
              animationIterationCount: 'infinite',
              background: p.color,
              boxShadow: `0 0 10px ${p.color}, 0 0 20px ${p.color}`,
              pointerEvents: 'none',
              zIndex: 1
            }}
          />
        ))}

        {/* Scan Line */}
        <div style={{
          position: 'fixed',
          height: '2px',
          width: '100%',
          left: 0,
          background: 'linear-gradient(90deg, transparent, #00f5ff, transparent)',
          animation: 'scanLine 8s linear infinite',
          pointerEvents: 'none',
          zIndex: 1
        }} />

        {/* Login Card */}
        <div style={{
          position: 'relative',
          zIndex: 10,
          width: '100%',
          maxWidth: '450px',
          background: 'rgba(10, 10, 15, 0.9)',
          border: '1px solid rgba(0, 245, 255, 0.2)',
          borderRadius: '20px',
          padding: '50px 40px',
          backdropFilter: 'blur(20px)',
          animation: 'borderPulse 3s ease-in-out infinite'
        }}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none' }}>
            <div style={{
              textAlign: 'center',
              marginBottom: '40px'
            }}>
              <Gamepad2 
                size={48} 
                style={{ 
                  color: '#00f5ff',
                  marginBottom: '15px',
                  animation: 'logoGlow 2s ease-in-out infinite alternate'
                }} 
              />
              <div style={{
                fontFamily: "'Orbitron', monospace",
                fontSize: '1.8rem',
                fontWeight: '900',
                background: 'linear-gradient(135deg, #00f5ff, #ff00ff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '4px'
              }}>
                CUGS//GAMES
              </div>
            </div>
          </Link>

          <h1 style={{
            fontFamily: "'Orbitron', monospace",
            fontSize: '1.5rem',
            fontWeight: '700',
            textAlign: 'center',
            marginBottom: '30px',
            color: '#fff'
          }}>
            WELCOME BACK, PLAYER
          </h1>

          {/* Error Message */}
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '15px',
              background: 'rgba(255, 50, 50, 0.1)',
              border: '1px solid rgba(255, 50, 50, 0.3)',
              borderRadius: '10px',
              marginBottom: '20px',
              animation: 'shake 0.3s ease-in-out'
            }}>
              <AlertCircle size={20} color="#ff3232" />
              <span style={{ color: '#ff3232', fontSize: '0.95rem' }}>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Email/Username Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Email or Username
              </label>
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                {isEmail ? (
                  <Mail 
                    size={20} 
                    style={{ 
                      position: 'absolute', 
                      left: '15px', 
                      color: '#00f5ff',
                      zIndex: 1,
                      transition: 'all 0.3s ease'
                    }} 
                  />
                ) : (
                  <User 
                    size={20} 
                    style={{ 
                      position: 'absolute', 
                      left: '15px', 
                      color: '#00f5ff',
                      zIndex: 1,
                      transition: 'all 0.3s ease'
                    }} 
                  />
                )}
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="player1 or player@cugs.com"
                  required
                  style={{
                    width: '100%',
                    padding: '15px 15px 15px 50px',
                    background: 'rgba(0, 245, 255, 0.05)',
                    border: '1px solid rgba(0, 245, 255, 0.2)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '1rem',
                    fontFamily: "'Rajdhani', sans-serif",
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#00f5ff';
                    e.target.style.boxShadow = '0 0 20px rgba(0, 245, 255, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(0, 245, 255, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            {/* Password Input */}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Password
              </label>
              <div style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Lock 
                  size={20} 
                  style={{ 
                    position: 'absolute', 
                    left: '15px', 
                    color: '#ff00ff',
                    zIndex: 1
                  }} 
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  style={{
                    width: '100%',
                    padding: '15px 50px 15px 50px',
                    background: 'rgba(255, 0, 255, 0.05)',
                    border: '1px solid rgba(255, 0, 255, 0.2)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '1rem',
                    fontFamily: "'Rajdhani', sans-serif",
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#ff00ff';
                    e.target.style.boxShadow = '0 0 20px rgba(255, 0, 255, 0.2)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 0, 255, 0.2)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '15px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'rgba(255, 255, 255, 0.5)',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '15px',
                background: isLoading 
                  ? 'rgba(0, 245, 255, 0.3)' 
                  : 'linear-gradient(135deg, #00f5ff, #ff00ff)',
                border: 'none',
                borderRadius: '10px',
                color: '#000',
                fontSize: '1.1rem',
                fontWeight: '700',
                fontFamily: "'Orbitron', monospace",
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                textTransform: 'uppercase',
                letterSpacing: '2px'
              }}
              onMouseOver={(e) => {
                if (!isLoading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 30px rgba(0, 245, 255, 0.3)';
                }
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              {isLoading ? (
                <div style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid rgba(0, 0, 0, 0.3)',
                  borderTopColor: '#000',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
              ) : (
                <>
                  <LogIn size={22} />
                  ENTER GAME
                </>
              )}
            </button>
          </form>

          {/* Demo Credentials */}
          {/* <div style={{
            marginTop: '25px',
            padding: '15px',
            background: 'rgba(0, 255, 136, 0.05)',
            border: '1px solid rgba(0, 255, 136, 0.2)',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            <div style={{ 
              color: '#00ff88', 
              fontSize: '0.85rem', 
              marginBottom: '5px',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              Demo Credentials
            </div>
            <div style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
              player1 / password123
            </div>
          </div> */}

          {/* Sign Up Link */}
          <p style={{
            textAlign: 'center',
            marginTop: '30px',
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '1rem'
          }}>
            New to CUGS Games?{' '}
            <Link 
              to="/signup" 
              style={{
                color: '#00f5ff',
                textDecoration: 'none',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.textShadow = '0 0 10px #00f5ff'}
              onMouseOut={(e) => e.target.style.textShadow = 'none'}
            >
              Create Account
            </Link>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
